import * as React from "react"

import LoadingOverlay, { LoadingOverlayProps } from "@/shared/ui/loading/LoadingOverlay"

import { cn } from "@/shared/lib/utils"

export interface WithLoadingProps extends Omit<LoadingOverlayProps, "children"> {
  /**
   * Whether the component should be disabled when loading
   */
  disableWhenLoading?: boolean

  /**
   * Additional class names to apply when loading
   */
  loadingClassName?: string
}

/**
 * Higher-order component that adds loading functionality to any component
 *
 * @example
 * const LoadingButton = withLoading(Button);
 *
 * // Then use as:
 * <LoadingButton isLoading={true} onClick={handleClick}>
 *   Click Me
 * </LoadingButton>
 */
export function withLoading<P extends object>(
  Component: React.ComponentType<P>,
  defaultLoadingProps?: Partial<WithLoadingProps>
) {
  const displayName = Component.displayName || Component.name || "Component"

  const WithLoading = React.forwardRef<unknown, P & WithLoadingProps>((props, ref) => {
    const {
      isLoading = false,
      disableWhenLoading = true,
      loadingClassName,
      overlayClassName,
      spinnerWrapperClassName,
      backdropClassName,
      spinnerSize,
      spinnerColor,
      spinner,
      ...componentProps
    } = props

    // Combine default props with passed props
    const loadingProps = {
      isLoading,
      overlayClassName,
      spinnerWrapperClassName,
      backdropClassName,
      spinnerSize,
      spinnerColor,
      spinner,
      ...defaultLoadingProps
    }

    // Filter out non-component props for type safety
    const filteredProps = componentProps as unknown as P

    const disabled = disableWhenLoading && isLoading ? true : (filteredProps as any).disabled

    return (
      <LoadingOverlay {...loadingProps}>
        <Component
          {...filteredProps}
          className={cn((filteredProps as any).className, isLoading && loadingClassName)}
          disabled={disabled}
          ref={ref}
        />
      </LoadingOverlay>
    )
  })

  WithLoading.displayName = `WithLoading(${displayName})`

  return WithLoading
}

// Export as a named and default export
export default withLoading
